import React from 'react';
import { Box, Tooltip, IconButton, useTheme } from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const CodiCodeBlock = ({ inline, className, children, ...props }) => {
  const [copied, setCopied] = React.useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const codeString = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString);
      setCopied(true);
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = codeString;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
      } catch (_) {}
    }
    setTimeout(() => setCopied(false), 2000);
  };

  if (inline) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  return (
    <Box sx={{ position: 'relative', mb: 1 }}>
      <Tooltip title={copied ? 'Kopiert!' : 'Code kopieren'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          aria-label="Code kopieren"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            '&:hover': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)' }
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 16, color: '#4caf50' }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 16, color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#666' }} />
          )}
        </IconButton>
      </Tooltip>

      <SyntaxHighlighter
        language={language || 'text'}
        style={isDark ? oneDark : oneLight}
        customStyle={{
          borderRadius: '12px',
          padding: '16px',
          fontSize: '0.9em',
          margin: 0,
          backgroundColor: isDark ? '#2C2C2C' : '#fafafa',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
          boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.06)'
        }}
        {...props}
      >
        {codeString}
      </SyntaxHighlighter>
    </Box>
  );
};

export default CodiCodeBlock;
